"use client";

import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TenancyProvider } from "@/lib/tenancy/tenancy-context";
import { AuthProvider } from "@/lib/auth/auth-context";
import { ApiError, RequestTimeoutError } from "@/lib/api/http";
import { isNotServedYet } from "@/lib/api/api-keys";

/**
 * What to retry.
 *
 * react-query retries three times by default, with backoff. Against this
 * gateway that is wrong twice over, and it was MEASURED rather than reasoned:
 * signed in against the live platform, a 403 on the run list left the Scans
 * page in a loading state for the whole backoff, so the surface that must
 * report a refusal reported nothing for seven seconds.
 *
 * A refusal is not transient. The error envelope carries `retryable` and says
 * so itself (SDD Appendix B), so this reads the platform's answer rather than
 * inferring one from a status code. An error that is not an envelope never
 * reached the handler — a dropped connection — and gets one retry, because
 * nothing has claimed either way.
 *
 * A not-served error is the client's own "http mode has no route for this" and
 * is as permanent as a refusal — retrying it only makes a page that says "not
 * served" wait out the backoff first, flashing an empty table meanwhile
 * (S-253). It gets no retry.
 *
 * A read the platform did not answer within REQUEST_TIMEOUT_MS gets no retry
 * either (S-1063, found by its independent review): a retry would wait the
 * whole bound again, and a save that re-reads after a failure would then hold
 * "Saving…" for minutes rather than one bound.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isNotServedYet(error)) return false;
  if (error instanceof RequestTimeoutError) return false;
  if (error instanceof ApiError) return error.retryable && failureCount < 2;
  return failureCount < 2;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = React.useState(
    () => new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          refetchOnWindowFocus: false,
          retry: shouldRetry,
        },
      },
    }),
  );
  return (
    // Auth outermost: the bearer token must be set before any query runs, or
    // the first request of every session goes out unauthenticated.
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <TenancyProvider>{children}</TenancyProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
